import { CopyIcon, DeleteIcon } from '@chakra-ui/icons'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent, ModalHeader,
  ModalOverlay
} from '@chakra-ui/modal'
import {
  Accordion, AccordionButton, AccordionItem, AccordionPanel, Button, Center, Divider,
  ModalFooter, HStack, Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  LinkBox,
  LinkOverlay,
  Popover,
  PopoverArrow, PopoverBody, PopoverCloseButton,
  PopoverContent, PopoverFooter, PopoverTrigger,
  Spinner, Tab, TabList, TabPanel, TabPanels, Tabs, Text,
  useClipboard, useDisclosure,
} from '@chakra-ui/react'
import { Form, Formik } from 'formik'
import Yup from "yup"
import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { AiOutlineEdit } from 'react-icons/ai'
import { MdOutlineAddLink } from 'react-icons/md'
import useFetch, { CachePolicies, Req } from 'use-http'
import useToast from '../../../hooks/useToast'
import type {
  PublicLinkResponse,
  PublicLinksResponse
} from '../../../interfaces'
import { UserContext } from '../../../providers'
import usePublicLinkForm from '../PublicLinks/usePublicLinkForm'
import { PublicLinksForAClientResponse } from '../../../interfaces/PublicLinks'

interface FormProps {
  assetType: string
  validFrom: string | null,
  validTill: string | null,
  password?: string,
  exposeDraftAssets?: boolean,
}

const FormAccordionItem = ({
  request,
  assetType,
  publicLink,
  formFieldComponents,
  formValidationSchema,
  getFormikInitialValues,
  createOrUpdatePublicLink,
  deletePublicLink,
  getUrl,
  sideEffects,
  setTabIndex,
}: {
  request: Req<PublicLinksResponse>
  assetType: string
  publicLink: PublicLinkResponse
  formFieldComponents: React.ReactElement
  formValidationSchema: Yup.ObjectSchema<any>
  getFormikInitialValues: (args: {
    publicLink: PublicLinkResponse | undefined
    assetType: string
  }) => FormProps
  createOrUpdatePublicLink: (args: {
    formValues: FormProps,
    publicLink: PublicLinkResponse | undefined
    assetId: string
    getUrl: string
    sideEffects: (response: PublicLinksResponse | PublicLinksForAClientResponse | undefined) => void
  }) => Promise<void>
  deletePublicLink: (args: {
    publicLink: PublicLinkResponse
    getUrl: string
    sideEffects: (response: PublicLinksResponse | PublicLinksForAClientResponse | undefined) => void
  }) => Promise<void>
  getUrl: string
  sideEffects: (response: PublicLinksResponse | PublicLinksForAClientResponse | undefined) => void
  setTabIndex: React.Dispatch<React.SetStateAction<number>>
}): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const { onCopy: onCopyPublicLinkURL } = useClipboard(publicLink.publicURL)
  const {
    isOpen: isOpenDeletePopover,
    onOpen: onOpenDeletePopover,
    onClose: onCloseDeletePopover,
  } = useDisclosure()

  return (
    <AccordionItem border="none">
      <HStack paddingBottom="1rem">
        <LinkBox flexGrow="1">
          <LinkOverlay href={publicLink?.publicURL} isExternal>
          <InputGroup>
            <Input
              isTruncated
              placeholder={publicLink?.publicURL}
              disabled
              _disabled={{}}
              background="white"
            />
              <InputRightElement zIndex="0">
                <IconButton
                  icon={<CopyIcon />}
                  size="sm"
                  aria-label="Copy url"
                  color="gray.800"
                  background="gray.100"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onCopyPublicLinkURL()
                    toast({
                      description: t('copied', 'Copied'),
                      status: 'success',
                      position: 'top-right',
                      isClosable: true,
                      duration: 5000,
                    })
                  }}
                  _hover={{ background: "gray.200" }}
                />
              </InputRightElement>
          </InputGroup>
          </LinkOverlay>
        </LinkBox>
        <Popover
          isOpen={isOpenDeletePopover}
          onClose={onCloseDeletePopover}
          placement="top"
        >
          <PopoverTrigger>
            <IconButton
              aria-label="Delete public link"
              icon={<DeleteIcon />}
              onClick={onOpenDeletePopover}
              variant="deleteCTA"
              border="none"
            />
          </PopoverTrigger>
          <PopoverContent>
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverBody>
              <Text maxWidth="90%">
                {t("are_you_sure_public_link_delete", "Are you sure you want to delete this public link?")}
              </Text>
            </PopoverBody>
            <PopoverFooter as={HStack} justifyContent="end">
              <Button
                type="button"
                colorScheme="appRed"
                isLoading={request.loading}
                isDisabled={request.loading}
                onClick={async () => {
                  await deletePublicLink({
                    publicLink,
                    getUrl,
                    sideEffects,
                  })
                  onCloseDeletePopover()
                }}
              >
                {t("delete", "Delete")}
              </Button>
            </PopoverFooter>
          </PopoverContent>
        </Popover>
        <AccordionButton width="35px" height="35px" justifyContent="center">
          <Icon
            as={AiOutlineEdit}
            color="appBattleshipGrey"
            background="unset"
            _hover={{ background: "unset" }}
          />
        </AccordionButton>
      </HStack>
      <AccordionPanel paddingBottom="1rem">
        <Formik<FormProps>
          enableReinitialize
          initialValues={getFormikInitialValues({
            publicLink,
            assetType,
          })}
          validationSchema={formValidationSchema}
          onSubmit={async (formValues) => {
            await createOrUpdatePublicLink({
              formValues,
              publicLink,
              assetId: publicLink.assetInfo.id,
              getUrl,
              sideEffects,
            })
            setTabIndex(1)
          }}
        >
          {({ dirty, isSubmitting, submitForm }) => (
            <Form>
              {formFieldComponents}
              <HStack justifyContent="end">
                <Button
                  type="submit"
                  size="sm"
                  isDisabled={
                    (publicLink && !dirty) ||
                    isSubmitting
                  }
                  isLoading={isSubmitting}
                  onClick={async () => {
                    await submitForm()
                  }}
                >
                  {t('submit', 'Submit')}
                </Button>
              </HStack>
            </Form>
          )}
        </Formik>
      </AccordionPanel>
    </AccordionItem>
  )
}

const PublicLinksModal = ({
  assetType,
  assetId,
  assetName,
  isOpen,
  onClose,
}: {
  assetId: string
  assetType: string
  assetName: string
  isOpen: boolean
  onClose(): void
}): React.ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const [ tabIndex, setTabIndex ] = useState(0)
  const [ publicLinks, setPublicLinks ] = useState<PublicLinksResponse | undefined>(undefined)
  const { request } = useFetch<PublicLinksResponse>({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const {
    formFieldComponents,
    getFormikInitialValues,
    formValidationSchema,
    getPublicLinks,
    createOrUpdatePublicLink,
    deletePublicLink,
  } = usePublicLinkForm({
    request,
  })

  const getUrl = useMemo(() => {
    return `/api/media-library/${user?.clientNumber}/assets/${assetId}/public-links`
  }, [user?.clientNumber, assetId])

  const getPublicLinksSideEffects = useCallback((response: PublicLinksResponse | PublicLinksForAClientResponse | undefined) => {
    if (Array.isArray(response)) {
      setPublicLinks(response ?? [])
    }
  }, [setPublicLinks])

  useEffect(() => {
    if (assetId && isOpen) {
      void getPublicLinks({
        url: getUrl,
        sideEffects: getPublicLinksSideEffects,
      })
    }
  }, [assetId, isOpen, getUrl, getPublicLinksSideEffects])

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setTabIndex(0)
        onClose()
      }}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          enableReinitialize
          initialValues={getFormikInitialValues({
            publicLink: undefined,
            assetType,
          })}
          validationSchema={formValidationSchema}
          onSubmit={async (formValues) => {
            await createOrUpdatePublicLink({
              formValues,
              publicLink: undefined,
              assetId,
              getUrl,
              sideEffects: getPublicLinksSideEffects,
            })
            setTabIndex(1)
          }}
        >
          {({ isSubmitting, submitForm, dirty }) => (
            <Form>
              <ModalHeader fontSize="2xl">
                <HStack spacing="0.5em">
                  <Icon
                    as={MdOutlineAddLink}
                    color="appBattleshipGrey"
                    backgroundColor="appPaleGrey"
                    boxSize={6}
                    padding="0.1em"
                    borderRadius="full"
                    display="inline"
                  />
                  <Trans t={t} i18nKey="public_links_modal_heading" values={{
                    assetType: assetType === "media-collection" ? "media collection" : assetType,
                    assetName,
                  }}>
                    <Text isTruncated maxWidth="80%">
                      Public links for {assetType === "media-collection" ? "media collection" : assetType}{" "}{`'${assetName}`}
                    </Text>
                  </Trans>
                </HStack>
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {
                  request.loading || !publicLinks ? (
                    <Spinner />
                  ) : (
                    <Tabs index={tabIndex} onChange={setTabIndex}>
                      <TabList>
                        <Tab>{t("create", "Create")}</Tab>
                        <Tab>{t("manage", "Manage")}</Tab>
                      </TabList>
                      <TabPanels>
                        <TabPanel paddingLeft="0" paddingRight="0">
                          {formFieldComponents}
                        </TabPanel>
                        <TabPanel paddingLeft="0" paddingRight="0">
                          <Accordion allowMultiple>
                            {
                              publicLinks?.length > 0 ? (
                                publicLinks.map((publicLink) => (
                                  <React.Fragment key={publicLink._id}>
                                    <FormAccordionItem
                                      request={request}
                                      assetType={assetType}
                                      publicLink={publicLink}
                                      formFieldComponents={formFieldComponents}
                                      formValidationSchema={formValidationSchema}
                                      getFormikInitialValues={getFormikInitialValues}
                                      createOrUpdatePublicLink={createOrUpdatePublicLink}
                                      deletePublicLink={deletePublicLink}
                                      getUrl={getUrl}
                                      sideEffects={getPublicLinksSideEffects}
                                      setTabIndex={setTabIndex}
                                    />
                                    <Divider marginBottom="1rem"/>
                                  </React.Fragment>
                                ))
                              ) : (
                                <Center>
                                  <Text>
                                    {t("no_public_links", "No public links found.")}
                                  </Text>
                                </Center>
                              )
                            }
                          </Accordion>
                        </TabPanel>
                      </TabPanels>
                    </Tabs>
                  )
                }
              </ModalBody>
              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <>
                  <Button
                    onClick={() => {
                      setTabIndex(0)
                      onClose()
                    }}
                    variant="outline"
                  >
                    {t('cancel', 'Cancel')}
                  </Button>
                  {
                    tabIndex === 0 ? (
                      <Button
                        colorScheme="appBlue"
                        isDisabled={
                          isSubmitting
                        }
                        isLoading={isSubmitting}
                        loadingText={t("submitting_loading_state_button_label", "Submitting...")}
                        onClick={async () => {
                          await submitForm()
                        }}
                      >
                        {t('submit', 'Submit')}
                      </Button>
                    ) : null
                  }
                </>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default PublicLinksModal
